import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import { observer } from 'mobx-react';

import downloadDocumentIcon from 'core/assets/svg/download-doc.svg';
import { AuthContext } from 'core/components/auth';
import { Loading } from 'core/components/loading';
import { useStore } from 'core/store';
import { OfferStatus } from 'core/store/offer-store';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { ActionButton } from 'core/components/action-button';
import Section from 'core/components/section';
import { Label } from 'core/components/label';
import { FormField } from 'core/components/form';
import { currencyFormatter } from 'core/helpers/formatters';
import { Tooltip } from 'core/components/tooltip';
import closeIcon from './icon-orange-close.svg';
import arrowIcon from 'core/assets/svg/up-right-arrow.svg';
import { tooltipHoverTexts } from 'core/helpers/constants';
import useAutoPremiumBreakdown from './use-auto-premium-breakdown';
import useStyles from './auto-breakdown-preview.styles';
import getQuoteFriendlySelectedOption from 'offer/helpers/quote-friendly-selected-option';

const formatCoverage = (coverage) => {
  if (typeof coverage === 'boolean') {
    return coverage ? 'Purchased' : 'Not Purchased';
  }
  if (coverage === 'true') {
    return 'Purchased';
  }
  return isNaN(coverage) ? coverage || 'None' : currencyFormatter(coverage);
};

const otherCoverageTitle = {
  trailerTotal: 'Trailer(s)',
  rentersTotal: 'Renters'
};

//all values coming back from clarion Door are strings that need to be converted to numbers for math
const convertToNumber = (numberTotal) =>
  typeof numberTotal === 'string' ? parseFloat(numberTotal.replace(/[,$]/g, '')) : numberTotal;

function AutoBreakdownPreview({
  offer: policyOffer,
  policyType,
  cars,
  trailers,
  autoCoverage,
  triggerText,
  premiumBreakdown
}) {
  const { viewOnly } = useContext(AuthContext);
  const {
    offer: offerStore,
    account: {
      policies: { policy }
    }
  } = useStore();
  const { premium, surplusContribution, totalFeeCost } = policy;
  const [showAutoBreakdown, setShowAutoBreakdown] = useState(false);
  const classes = useStyles();

  const { downloadOffer } = offerStore;
  const { inOfferEditRoute } = useRouteHelper();
  const offer = !inOfferEditRoute ? policyOffer : offerStore.offer;
  const trailersOrEmpty = Array.isArray(trailers) ? trailers : [];

  const mergedVehicles = [...cars, ...trailersOrEmpty]; // cars has to be first in the array to use the below hook correctly
  const { premiumCarsCoverages, premiumOtherCoverages } = useAutoPremiumBreakdown(
    offer,
    policyType,
    premiumBreakdown,
    cars,
    mergedVehicles
  );

  // making array here removes VINs key from the object
  const otherPremiumsArray = Object.values(premiumOtherCoverages);
  const carPremiumArray = Object.values(premiumCarsCoverages);

  let totalTermPrice = 0;

  const getVehiclePrice = (vin, carCoverageName) => {
    const vehiclePrice = convertToNumber(premiumCarsCoverages?.[vin]?.[carCoverageName]);

    const carSubTotal = carPremiumArray.reduce((acc, vehicle) => {
      const numberValue = convertToNumber(vehicle.vehiclePrice);

      return acc + numberValue;
    }, 0);
    const { autoTotal: offerTotal } =
      offer?.options?.find((o) => o.type === getQuoteFriendlySelectedOption(policyType)) || {};
    const policyTotal = premium + surplusContribution + totalFeeCost;
    const totalPrice = !inOfferEditRoute ? policyTotal : offerTotal;

    const amountToAdd = convertToNumber((totalPrice - carSubTotal) / cars.length);

    const perVehicleTotal = vehiclePrice + amountToAdd;

    totalTermPrice += perVehicleTotal;

    return currencyFormatter(perVehicleTotal);
  };

  const getOtherPremiumTotal = (otherCoverageName) => {
    const total = otherPremiumsArray
      .filter((v) => v[otherCoverageName])
      .reduce((acc, currCoverage) => {
        const numberValue = convertToNumber(currCoverage[otherCoverageName]);

        totalTermPrice += numberValue;

        return acc + numberValue;
      }, 0);

    return currencyFormatter(total);
  };
  const otherCoverageName = otherPremiumsArray.map((v) => Object.keys(v));
  const otherPremiumCardTitle = [...new Set(otherCoverageName.map((v) => otherCoverageTitle[v]))]; //removes duplicate other coverage cards showing

  const getAutoCoverageWithPremium = (prop) =>
    autoCoverage[prop]?.split('/')?.reduce((s, v, i) => `${s}${i !== 0 ? '/' : ''}${currencyFormatter(v)}K`, '') ||
    'None';

  return (
    <>
      <Button
        color={inOfferEditRoute ? 'primary' : 'secondary'}
        variant={inOfferEditRoute ? 'contained' : 'text'}
        className={classes.trigger}
        onClick={() => {
          setShowAutoBreakdown(true);
        }}
      >
        {inOfferEditRoute && <img src={arrowIcon} alt="checklist" />}
        {triggerText}
      </Button>
      <Dialog open={showAutoBreakdown} fullWidth maxWidth="md">
        <DialogContent className={classes.modal}>
          <ActionButton
            type="edit"
            size="big"
            icon={closeIcon}
            className={classes.closeButton}
            onClick={() => setShowAutoBreakdown(false)}
          />
          <Grid container alignContent="center" justifyContent="flex-start">
            <Grid item>
              <Label className={classes.title} type="greenBig">
                Auto premium breakdown
              </Label>
            </Grid>
            {!viewOnly && inOfferEditRoute && (
              <Grid item>
                <Button
                  color="secondary"
                  variant="text"
                  disabled={offerStore.status === OfferStatus.DownloadingOffer}
                  onClick={() => {
                    downloadOffer({
                      offerId: offer?.id,
                      clusterId: offer?.clusterId,
                      code: offer?.code,
                      policyType
                    });
                  }}
                >
                  <Grid container>
                    {offerStore.status === OfferStatus.DownloadingOffer ? (
                      <Loading noBackground size={20} className={classes.loader} />
                    ) : (
                      <img src={downloadDocumentIcon} alt="download-document-icon" />
                    )}
                    Download PDF
                  </Grid>
                </Button>
              </Grid>
            )}
          </Grid>

          <Formik initialValues={{}}>
            <>
              <Section title="Shared coverages" type="Section">
                <div className={classes.container}>
                  <Grid container className={classes.containerDark} spacing={4}>
                    <FormField
                      mode="dark"
                      name={'Property-Damage-Limit'}
                      label="Bodily Injury/ Property Damage Limit"
                      type="value"
                      value={getAutoCoverageWithPremium('policyLimitBIPD')}
                      xs={6}
                    />
                    <FormField
                      mode="dark"
                      name={'Medical-Payment-Per-Person'}
                      label="Medical Payment Per Person"
                      type="value"
                      value={getAutoCoverageWithPremium('policyLimitMedicalPayments')}
                      xs={6}
                    />
                    <FormField
                      mode="dark"
                      name={'Uninsured-Motorists'}
                      label="Uninsured Motorists BI"
                      type="value"
                      value={getAutoCoverageWithPremium('policyLimitUMBI')}
                      xs={6}
                    />
                    <FormField
                      mode="dark"
                      name={'Under-Insured-Motorists'}
                      label="Underinsured Motorists BI"
                      type="value"
                      value={getAutoCoverageWithPremium('policyLimitUIMBI')}
                      xs={6}
                    />
                  </Grid>
                </div>
              </Section>

              <Section
                title="Cars"
                type="Section"
                rightLabel="Total"
                rightValue={`${cars.length} ${cars.length === 1 ? 'car' : 'cars'}`}
              >
                {cars.map((car, i) => (
                  <div className={classes.container} key={i}>
                    <Grid container className={classes.containerDark} spacing={4}>
                      <Grid
                        container
                        xs={12}
                        className={classes.titleLine}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Label className={classes.readOnly} type="infoSubtitle">
                          {`${car?.year} ${car?.make} ${car?.model}`}
                        </Label>
                        <Grid container direction="column" alignItems="flex-end" xs={2}>
                          <Label type="coverageLabel">
                            <Grid container alignItems="center" justifyContent="center">
                              Vehicle Price
                              <Tooltip
                                className={classes.tooltip}
                                text={tooltipHoverTexts.individualVehiclePrice}
                                smallIcon
                              />
                            </Grid>
                          </Label>
                          <Label type="coverageValue" className={classes.readOnly}>
                            {getVehiclePrice(car.VIN, 'vehiclePrice')}
                          </Label>
                        </Grid>
                      </Grid>

                      <FormField
                        mode="dark"
                        name={`Collision-Deductible-${i}`}
                        label="Collision Deductible"
                        type="value"
                        value={formatCoverage(car.deductibleCollision)}
                        xs={4}
                      />
                      <FormField
                        mode="dark"
                        name={`Comprehensive-Deductible-${i}`}
                        label="Comprehensive Deductible"
                        type="value"
                        value={formatCoverage(car.deductibleComprehensive)}
                        xs={4}
                      />
                      <FormField
                        mode="dark"
                        name={`Additional-Custom-Parts-Limit-${i}`}
                        label="Additional Custom Parts Limit"
                        type="value"
                        value={formatCoverage(car.limitACPE)}
                        xs={4}
                      />
                      <FormField
                        mode="dark"
                        name={`Umuim-Property-Damage-Limit-${i}`}
                        label="UM/UIM Property Damage Limit"
                        type="value"
                        value={formatCoverage(car.limitUMPD)}
                        xs={4}
                      />
                      <FormField
                        mode="dark"
                        name={`Daily-Rental-Car-Limit-${i}`}
                        label="Daily Rental Car Limit"
                        type="value"
                        value={car.limitRental || 'None'}
                        xs={8}
                      />
                      <FormField
                        mode="dark"
                        name={`Roadside-Assistance-${i}`}
                        label="Roadside Assistance"
                        type="value"
                        value={car.roadsideAssistance ? 'Yes' : 'No'}
                        xs={4}
                      />
                      <FormField
                        mode="dark"
                        name={`Ride-Sharing-${i}`}
                        label="Ride Sharing"
                        type="value"
                        value={car.rideSharing ? 'Yes' : 'No'}
                        xs={4}
                      />
                      <FormField
                        mode="dark"
                        name={`LL-Gap-Coverage-${i}`}
                        label="Lease/Loan Gap Coverage"
                        type="value"
                        value={car.coverageLoan ? 'Yes' : 'No'}
                        xs={4}
                      />
                    </Grid>
                  </div>
                ))}
              </Section>

              {otherPremiumsArray.length > 0 && (
                <Section
                  title="Other"
                  type="Section"
                  rightLabel="Total"
                  rightValue={`${otherPremiumCardTitle.length} ${otherPremiumCardTitle.length === 1 ? 'item' : 'items'}`}
                >
                  {otherPremiumCardTitle.map((title, i) => (
                    <div className={classes.container} key={i}>
                      <Grid
                        container
                        xs={12}
                        className={classes.containerDark}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Label className={classes.readOnly} type="infoSubtitle">
                          {title}
                        </Label>
                        <Grid container direction="column" alignItems="flex-end" xs={2}>
                          <Label type="coverageLabel">
                            <Grid container alignItems="center" justifyContent="center">
                              Total Price
                            </Grid>
                          </Label>
                          <Label type="coverageValue" className={classes.readOnly}>
                            {getOtherPremiumTotal(otherCoverageName[i])}
                          </Label>
                        </Grid>
                      </Grid>
                    </div>
                  ))}
                </Section>
              )}

              <Grid container xs={12} className={classes.totalLine} alignItems="center" justifyContent="space-between">
                <Label type="subtitle">Total term Price</Label>
                <Label type="subtitle">{currencyFormatter(totalTermPrice)}</Label>
              </Grid>
            </>
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  );
}

AutoBreakdownPreview.propTypes = {
  offer: PropTypes.object,
  policyType: PropTypes.string,
  cars: PropTypes.array,
  trailers: PropTypes.array,
  autoCoverage: PropTypes.object,
  triggerText: PropTypes.string,
  premiumBreakdown: PropTypes.object,
  source: PropTypes.string
};

AutoBreakdownPreview.defaultProps = {
  policyType: 'A',
  cars: [],
  trailers: [],
  autoCoverage: {},
  offer: {},
  triggerText: 'See auto premium breakdown',
  premiumBreakdown: undefined,
  source: 'offer'
};

export default observer(AutoBreakdownPreview);
